import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "urql";
import { sortBy } from "lodash";

import {
    FONAV_CARRY_GROUPS,
    FONAV_CARRY_SETTLE_DAYS,
    FONAV_CDS_INDICES,
    FoNavCarryGroup,
    FoNavRateSource,
    SWAP_CURVE_DEFINITION_ID,
    SWAP_FLOATING_RATE_INSTRUMENT_ID
} from "./constants";
import { getFoNavFundRiskFactors } from "./fundChangeSources";
import {
    GET_FONAV_BONDS,
    GET_FONAV_CALENDAR,
    GET_FONAV_POSITIONS,
    GET_FONAV_SWAPS,
    GET_FONAV_TIMESERIES
} from "./queries";
import {
    FoNavBondRiskInput,
    FoNavCarryComponent,
    FoNavRiskFactorDefinition,
    FoNavRiskFactorDefaults,
    FoNavSwapRiskInput,
    addFoNavSettlementDays,
    aggregateFoNavCarryRate,
    applyFoNavCarryRateOffset,
    applyFoNavCarrySign,
    buildFoNavBondCarryComponents,
    buildFoNavBondRiskFactorDefaults,
    FoNavSebRateInput,
    applyFoNavSebSwapRates,
    buildFoNavBondRiskFactorDefinitions,
    buildFoNavBusinessDayCheck,
    buildFoNavCdsCarryComponents,
    buildFoNavCdsRiskFactorDefaults,
    buildFoNavCdsRiskFactorDefinitions,
    buildFoNavEstimateDurations,
    buildFoNavSwapCarryComponents,
    buildFoNavSwapRiskFactorDefaults,
    buildFoNavSwapRiskFactorDefinitions,
    calculateEstimatedNav,
    calculateFoNavCarry,
    calculateTotalRiskImpact,
    calculateWeightedRiskImpact,
    computeChangeBp,
    foNavCalendarDayCount,
    foNavNumberFormat,
    foNavSettlementCalendarYears,
    formatFoNavChangeInput,
    formatFoNavDurationInput,
    formatFoNavWeightInput,
    getFixedLegMaturityPeriod,
    getTimeSeriesValueAtDate,
    getTimeToMaturityYears,
    getTimeseriesValue,
    maturityPeriodToYears,
    parseFoNavNumericInput,
    rateToPercent,
    spreadToBp
} from "./utils";
import { useFoNavCdsSeries } from "./useFoNavCdsSeries";
import { FoNavEstimateKpiValues } from "./FoNavKpis";
import { FoNavInstrumentName } from "./FoNavInstrumentName";
import { CalendarNameEnum, InstrumentModelLegTypesEnum } from "../../types.generated";

const impactFormat = foNavNumberFormat("0.00%");

const SWAPTION_ID = "swaption";
const OTHER_ID = "other";

type RiskFactorInputs = Record<string, { duration: string; change: string; weight: string }>;

type EstimateRowKind = "header" | "subtotal" | "factor" | "carry" | "total";

interface EstimateDisplayRow {
    id: string;
    label: string;
    weightLabel: string;
    duration: string;
    change: string;
    impact: number | null;
    kind: EstimateRowKind;
    muted?: boolean;
    instrumentId?: string | null;
}

const inputClassName =
    "text-right w-16 min-h-0 h-[18px] py-0 px-1 m-0 text-[9pt] leading-[18px] bg-white border border-slate-300 rounded-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

function factorRows(
    definitions: FoNavRiskFactorDefinition[],
    inputs: RiskFactorInputs,
    impacts: Record<string, number | null>
): EstimateDisplayRow[] {
    return definitions.map((definition) => ({
        id: definition.id,
        label: definition.label,
        weightLabel: inputs[definition.id]?.weight ?? formatFoNavWeightInput(definition.weight),
        duration: inputs[definition.id]?.duration ?? "",
        change: inputs[definition.id]?.change ?? "",
        impact: impacts[definition.id],
        kind: "factor",
        instrumentId: definition.instrumentId
    }));
}

function formatImpact(value: number | null): string {
    if (value === null) {
        return "-";
    }
    return impactFormat(value) ?? "-";
}

function defaultsToInputs(defaults: FoNavRiskFactorDefaults[]): RiskFactorInputs {
    const inputs: RiskFactorInputs = {};
    defaults.forEach((row) => {
        inputs[row.id] = {
            duration: formatFoNavDurationInput(row.duration),
            change: formatFoNavChangeInput(row.change),
            weight: formatFoNavWeightInput(row.weight)
        };
    });
    return inputs;
}

function buildSwapRiskInputs(
    curveInstruments:
        | {
              instrument?: {
                  _id?: string;
                  name: string;
                  model?: { legs?: { _t?: string | null; maturityPeriod?: string | null }[] | null } | null;
                  startSeries?: unknown;
                  endSeries?: unknown;
              } | null;
          }[]
        | null
        | undefined,
    startDate: string,
    endDate: string
): FoNavSwapRiskInput[] {
    if (!curveInstruments) {
        return [];
    }

    return curveInstruments
        .map((item): FoNavSwapRiskInput | null => {
            const instrument = item.instrument;
            if (!instrument) {
                return null;
            }

            const maturityPeriod = getFixedLegMaturityPeriod(
                instrument.model?.legs as { _t?: InstrumentModelLegTypesEnum | null; maturityPeriod?: string | null }[] | null
            );
            const timeToMaturityYears = maturityPeriod ? maturityPeriodToYears(maturityPeriod) : null;

            return {
                instrumentId: instrument._id,
                name: instrument.name,
                maturityPeriod,
                timeToMaturityYears,
                startRate: getTimeSeriesValueAtDate(instrument.startSeries, startDate),
                endRate: getTimeSeriesValueAtDate(instrument.endSeries, endDate)
            };
        })
        .filter((row): row is FoNavSwapRiskInput => row !== null);
}

function findSwapRateChange(
    swapRiskInputs: FoNavSwapRiskInput[],
    instrumentName: string | undefined
): number | null {
    if (!instrumentName) {
        return null;
    }

    const instrument = swapRiskInputs.find((row) => row.name === instrumentName);
    if (!instrument) {
        return null;
    }

    return computeChangeBp(instrument.startRate, instrument.endRate);
}

interface CarryRow {
    group: FoNavCarryGroup;
    startSettlementDate: string | null;
    endSettlementDate: string | null;
    days: number | null;
    rate: number | null;
    weight: number;
    carry: number | null;
}

interface CarryGroupInput {
    components: FoNavCarryComponent[];
    /** Netted off the weighted average rate. Omitted when the group accrues its full rate. */
    rateOffset?: number | null;
    /** Carry takes the sign of this duration. Omitted when the group always accrues positive carry. */
    durationSign?: number | null;
}

function buildCarryRows(
    groupInputs: Record<FoNavCarryGroup, CarryGroupInput>,
    prevNavDate: string | null,
    currentNavDate: string | null,
    isBusinessDay: (date: string) => boolean
): CarryRow[] {
    return FONAV_CARRY_GROUPS.map((group) => {
        const groupInput = groupInputs[group];
        const settleDays = FONAV_CARRY_SETTLE_DAYS[group];
        const startSettlementDate = addFoNavSettlementDays(prevNavDate, settleDays, isBusinessDay);
        const endSettlementDate = addFoNavSettlementDays(currentNavDate, settleDays, isBusinessDay);
        const days = foNavCalendarDayCount(startSettlementDate, endSettlementDate);
        const { rate: averageRate, weight } = aggregateFoNavCarryRate(groupInput.components);
        const rate = applyFoNavCarryRateOffset(averageRate, groupInput.rateOffset);
        const carry = applyFoNavCarrySign(calculateFoNavCarry(days, rate, weight), groupInput.durationSign);

        return {
            group,
            startSettlementDate,
            endSettlementDate,
            days,
            rate,
            weight,
            carry
        };
    });
}

function carryRowLabel(row: CarryRow): string {
    if (!row.startSettlementDate || !row.endSettlementDate) {
        return row.group;
    }
    return `${row.group} (${row.startSettlementDate} → ${row.endSettlementDate})`;
}

export function FoNavEstimate({
    fundId,
    startDate,
    endDate,
    prevNavDate,
    currentNavDate,
    totalNav,
    rateSource = "internal",
    sebRates,
    onEstimateChange
}: FoNavEstimateProps): React.ReactElement {
    const [inputs, setInputs] = useState<RiskFactorInputs>({});

    const fundRiskFactors = useMemo(() => getFoNavFundRiskFactors(fundId), [fundId]);
    const bondNames = useMemo(() => Object.keys(fundRiskFactors?.bondWeights ?? {}), [fundRiskFactors]);
    const cdsIndices = useMemo(() => {
        const cdsWeights = fundRiskFactors?.cdsWeights ?? {};
        return FONAV_CDS_INDICES.filter((index) => typeof cdsWeights[index.key] !== "undefined");
    }, [fundRiskFactors]);

    const [{ fetching: positionsFetching, error: positionsError, data: positionsData }] = useQuery({
        query: GET_FONAV_POSITIONS,
        variables: {
            fundId,
            date: endDate
        },
        pause: !fundId || !endDate,
        requestPolicy: "network-only"
    });

    const [{ fetching: bondsFetching, error: bondsError, data: bondsData }] = useQuery({
        query: GET_FONAV_BONDS,
        variables: {
            startDate,
            endDate,
            nameIn: bondNames
        },
        pause: bondNames.length === 0 || !startDate || !endDate,
        requestPolicy: "network-only"
    });

    const [{ fetching: swapsFetching, error: swapsError, data: swapsData }] = useQuery({
        query: GET_FONAV_SWAPS,
        variables: {
            curveId: SWAP_CURVE_DEFINITION_ID,
            startDate,
            endDate
        },
        pause: !startDate || !endDate,
        requestPolicy: "network-only"
    });

    const { fetching: cdsFetching, error: cdsError, rows: cdsRiskInputs } = useFoNavCdsSeries(cdsIndices, startDate, endDate);

    const [{ fetching: swapFloatingRateFetching, error: swapFloatingRateError, data: swapFloatingRateData }] = useQuery({
        query: GET_FONAV_TIMESERIES,
        variables: {
            filter: {
                instrumentIdIn: [SWAP_FLOATING_RATE_INSTRUMENT_ID],
                typeIn: "Price(Close)",
                startDate,
                endDate: startDate
            }
        },
        pause: !startDate,
        requestPolicy: "network-only"
    });

    const swapFloatingRate = useMemo(() => {
        return getTimeseriesValue(swapFloatingRateData?.timeseries?.[0]?.values);
    }, [swapFloatingRateData]);

    const calendarYears = useMemo(() => {
        return foNavSettlementCalendarYears([prevNavDate ?? startDate, currentNavDate ?? endDate]);
    }, [prevNavDate, currentNavDate, startDate, endDate]);

    const [{ fetching: calendarFetching, error: calendarError, data: calendarData }] = useQuery({
        query: GET_FONAV_CALENDAR,
        variables: {
            names: [CalendarNameEnum.SwedenFixedIncome],
            years: calendarYears
        },
        pause: calendarYears.length === 0,
        requestPolicy: "cache-and-network"
    });

    const positionDurations = useMemo(() => {
        if (totalNav === null) {
            return null;
        }
        return buildFoNavEstimateDurations(positionsData?.positions, totalNav);
    }, [positionsData, totalNav]);

    const swapRiskInputs = useMemo(() => {
        const inputsFromCurve = buildSwapRiskInputs(swapsData?.curveDefinition?.definition?.instruments, startDate, endDate);
        if (rateSource !== "seb") {
            return inputsFromCurve;
        }
        return applyFoNavSebSwapRates(inputsFromCurve, sebRates);
    }, [swapsData, startDate, endDate, rateSource, sebRates]);

    const bondRiskInputs = useMemo((): FoNavBondRiskInput[] => {
        if (!bondsData?.instruments) {
            return [];
        }

        return sortBy(
            bondsData.instruments.map((instrument) => {
                return {
                    instrumentId: instrument._id,
                    isin: instrument.isin,
                    name: instrument.name,
                    maturityDate: instrument.maturityDate,
                    spreadStart: instrument.spreadStart,
                    spreadEnd: instrument.spreadEnd,
                    yieldStart: rateToPercent(instrument.yieldStart),
                    yieldEnd: rateToPercent(instrument.yieldEnd)
                };
            }),
            (bond) => getTimeToMaturityYears(bond.maturityDate, endDate) ?? Number.MAX_SAFE_INTEGER
        );
    }, [bondsData, endDate]);

    const bondFactorDefaults = useMemo(() => {
        return buildFoNavBondRiskFactorDefaults(
            bondRiskInputs,
            positionDurations?.Bond ?? null,
            fundRiskFactors?.bondWeights ?? {}
        );
    }, [bondRiskInputs, positionDurations, fundRiskFactors]);

    const swapFactorDefaults = useMemo(() => {
        return buildFoNavSwapRiskFactorDefaults(
            swapRiskInputs,
            fundRiskFactors?.swapTenorWeights ?? {},
            positionDurations?.Swaps ?? null
        );
    }, [swapRiskInputs, fundRiskFactors, positionDurations]);

    const cdsFactorDefaults = useMemo(() => {
        return buildFoNavCdsRiskFactorDefaults(
            cdsRiskInputs,
            fundRiskFactors?.cdsWeights ?? {},
            positionDurations?.CDS ?? null
        );
    }, [cdsRiskInputs, fundRiskFactors, positionDurations]);

    const swaptionChange = useMemo(() => {
        return findSwapRateChange(swapRiskInputs, fundRiskFactors?.swapInstrumentName);
    }, [swapRiskInputs, fundRiskFactors]);

    useEffect(() => {
        if (positionDurations === null) {
            return;
        }

        setInputs({
            [SWAPTION_ID]: {
                duration: formatFoNavDurationInput(positionDurations.Swaption),
                change: formatFoNavChangeInput(swaptionChange),
                weight: "100"
            },
            [OTHER_ID]: {
                duration: "",
                change: "",
                weight: "100"
            },
            ...defaultsToInputs(bondFactorDefaults),
            ...defaultsToInputs(swapFactorDefaults),
            ...defaultsToInputs(cdsFactorDefaults)
        });
    }, [
        positionDurations,
        swaptionChange,
        bondFactorDefaults,
        swapFactorDefaults,
        cdsFactorDefaults,
        fundId,
        startDate,
        endDate
    ]);

    const bondDefinitions = useMemo(() => {
        return buildFoNavBondRiskFactorDefinitions(bondRiskInputs, endDate, fundRiskFactors?.bondWeights ?? {});
    }, [bondRiskInputs, endDate, fundRiskFactors]);

    const swapDefinitions = useMemo(() => {
        return buildFoNavSwapRiskFactorDefinitions(
            swapRiskInputs,
            fundRiskFactors?.swapTenorWeights ?? {},
            positionDurations?.Swaps ?? null
        );
    }, [swapRiskInputs, fundRiskFactors, positionDurations]);

    const cdsDefinitions = useMemo(() => {
        return buildFoNavCdsRiskFactorDefinitions(
            cdsRiskInputs,
            fundRiskFactors?.cdsWeights ?? {},
            positionDurations?.CDS ?? null
        );
    }, [cdsRiskInputs, fundRiskFactors, positionDurations]);

    const factorImpacts = useMemo(() => {
        const impacts: Record<string, number | null> = {};

        const allIds = [
            SWAPTION_ID,
            OTHER_ID,
            ...bondDefinitions.map((row) => row.id),
            ...swapDefinitions.map((row) => row.id),
            ...cdsDefinitions.map((row) => row.id)
        ];

        allIds.forEach((id) => {
            const duration = parseFoNavNumericInput(inputs[id]?.duration);
            const change = parseFoNavNumericInput(inputs[id]?.change);
            const weightPct = parseFoNavNumericInput(inputs[id]?.weight);
            let weight: number | null = null;
            if (weightPct !== null) {
                weight = weightPct / 100;
            }
            impacts[id] = calculateWeightedRiskImpact(duration, weight, change);
        });

        return impacts;
    }, [inputs, bondDefinitions, swapDefinitions, cdsDefinitions]);

    const bondSubtotal = useMemo(() => {
        return calculateTotalRiskImpact(bondDefinitions.map((row) => factorImpacts[row.id]));
    }, [bondDefinitions, factorImpacts]);

    const swapSubtotal = useMemo(() => {
        return calculateTotalRiskImpact(swapDefinitions.map((row) => factorImpacts[row.id]));
    }, [swapDefinitions, factorImpacts]);

    const cdsSubtotal = useMemo(() => {
        return calculateTotalRiskImpact(cdsDefinitions.map((row) => factorImpacts[row.id]));
    }, [cdsDefinitions, factorImpacts]);

    const otherSubtotal = useMemo(() => {
        return calculateTotalRiskImpact([factorImpacts[SWAPTION_ID], factorImpacts[OTHER_ID]]);
    }, [factorImpacts]);

    const calendarEvents = calendarData?.calendar?.calendarEvents;
    const calendarEventsMissing = !calendarEvents || calendarEvents.length === 0;

    const isBusinessDay = useMemo(() => {
        return buildFoNavBusinessDayCheck(calendarEvents);
    }, [calendarEvents]);

    const carryRows = useMemo(() => {
        return buildCarryRows(
            {
                Bonds: { components: buildFoNavBondCarryComponents(bondRiskInputs, fundRiskFactors?.bondWeights ?? {}) },
                Swaps: {
                    components: buildFoNavSwapCarryComponents(swapRiskInputs, fundRiskFactors?.swapTenorWeights ?? {}),
                    rateOffset: swapFloatingRate,
                    durationSign: positionDurations?.Swaps ?? null
                },
                CDS: { components: buildFoNavCdsCarryComponents(cdsRiskInputs, fundRiskFactors?.cdsWeights ?? {}) }
            },
            prevNavDate ?? startDate,
            currentNavDate ?? endDate,
            isBusinessDay
        );
    }, [
        bondRiskInputs,
        swapRiskInputs,
        cdsRiskInputs,
        fundRiskFactors,
        swapFloatingRate,
        positionDurations,
        prevNavDate,
        currentNavDate,
        startDate,
        endDate,
        isBusinessDay
    ]);

    const carrySubtotal = useMemo(() => {
        return calculateTotalRiskImpact(carryRows.map((row) => row.carry));
    }, [carryRows]);

    const totalImpact = useMemo(() => {
        return calculateTotalRiskImpact([bondSubtotal, swapSubtotal, cdsSubtotal, otherSubtotal, carrySubtotal]);
    }, [bondSubtotal, swapSubtotal, cdsSubtotal, otherSubtotal, carrySubtotal]);

    const estimatedNav = useMemo(() => {
        return calculateEstimatedNav(totalImpact, totalNav);
    }, [totalImpact, totalNav]);

    const estimateLoading =
        positionsFetching ||
        bondsFetching ||
        swapsFetching ||
        cdsFetching ||
        calendarFetching ||
        swapFloatingRateFetching;

    useEffect(() => {
        if (!onEstimateChange) {
            return;
        }
        if (estimateLoading || totalNav === null) {
            onEstimateChange({
                estimatedChangePct: null,
                estimatedNav: null,
                estimateLoading
            });
            return;
        }
        onEstimateChange({
            estimatedChangePct: totalImpact,
            estimatedNav,
            estimateLoading: false
        });
    }, [onEstimateChange, totalImpact, estimatedNav, estimateLoading, totalNav]);

    const displayRows = useMemo((): EstimateDisplayRow[] => {
        return [
            { id: "bonds-header", label: "Bonds", weightLabel: "", duration: "", change: "", impact: null, kind: "header" },
            ...factorRows(bondDefinitions, inputs, factorImpacts),
            { id: "bonds-subtotal", label: "", weightLabel: "", duration: "", change: "", impact: bondSubtotal, kind: "subtotal" },
            { id: "swaps-header", label: "Swaps", weightLabel: "", duration: "", change: "", impact: null, kind: "header" },
            ...factorRows(swapDefinitions, inputs, factorImpacts),
            { id: "swaps-subtotal", label: "", weightLabel: "", duration: "", change: "", impact: swapSubtotal, kind: "subtotal" },
            { id: "cds-header", label: "CDS", weightLabel: "", duration: "", change: "", impact: null, kind: "header" },
            ...factorRows(cdsDefinitions, inputs, factorImpacts),
            { id: "cds-subtotal", label: "", weightLabel: "", duration: "", change: "", impact: cdsSubtotal, kind: "subtotal" },
            { id: "other-header", label: "Other", weightLabel: "", duration: "", change: "", impact: null, kind: "header" },
            {
                id: SWAPTION_ID,
                label: "Swaption",
                weightLabel: inputs[SWAPTION_ID]?.weight ?? "",
                duration: inputs[SWAPTION_ID]?.duration ?? "",
                change: inputs[SWAPTION_ID]?.change ?? "",
                impact: factorImpacts[SWAPTION_ID],
                kind: "factor"
            },
            {
                id: OTHER_ID,
                label: "Other",
                weightLabel: inputs[OTHER_ID]?.weight ?? "",
                duration: inputs[OTHER_ID]?.duration ?? "",
                change: inputs[OTHER_ID]?.change ?? "",
                impact: factorImpacts[OTHER_ID],
                kind: "factor"
            },
            { id: "other-subtotal", label: "", weightLabel: "", duration: "", change: "", impact: otherSubtotal, kind: "subtotal" },
            { id: "carry-header", label: "Carry", weightLabel: "", duration: "", change: "", impact: null, kind: "header" },
            ...carryRows.map((row) => ({
                id: `carry-${row.group}`,
                label: carryRowLabel(row),
                weightLabel: formatFoNavWeightInput(row.weight),
                duration: row.days === null ? "" : String(row.days),
                change: formatFoNavChangeInput(spreadToBp(row.rate)),
                impact: row.carry,
                kind: "carry" as const
            })),
            { id: "carry-subtotal", label: "", weightLabel: "", duration: "", change: "", impact: carrySubtotal, kind: "subtotal" },
            {
                id: "total",
                label: "Total",
                weightLabel: "",
                duration: "",
                change: "",
                impact: totalImpact,
                kind: "total"
            }
        ];
    }, [
        inputs,
        bondDefinitions,
        swapDefinitions,
        cdsDefinitions,
        factorImpacts,
        bondSubtotal,
        swapSubtotal,
        cdsSubtotal,
        otherSubtotal,
        carryRows,
        carrySubtotal,
        totalImpact
    ]);

    const updateInput = (id: string, field: "duration" | "change" | "weight", value: string): void => {
        setInputs((current) => ({
            ...current,
            [id]: {
                ...current[id],
                [field]: value
            }
        }));
    };

    const fetching = estimateLoading;
    const error =
        positionsError ||
        bondsError ||
        swapsError ||
        cdsError ||
        calendarError ||
        swapFloatingRateError;

    if (fetching) {
        return <p>Loading NAV estimate...</p>;
    }

    if (error) {
        return <p>error: {JSON.stringify(error, null, 2)}</p>;
    }

    if (totalNav === null) {
        return <p>No saved NAV found on or before {startDate}.</p>;
    }

    return (
        <div>
            <table className="table table-xs table-striped w-auto">
                <thead>
                    <tr>
                        <th className="left">Risk factor</th>
                        <th className="right">Weight (%)</th>
                        <th className="right">Duration</th>
                        <th className="right">Change (bp)</th>
                        <th className="right">Impact</th>
                    </tr>
                </thead>
                <tbody>
                    {displayRows.map((row) => {
                        if (row.kind === "header") {
                            return (
                                <tr key={row.id} className="bg-gray-100">
                                    <td className="nowrap left">
                                        <strong>{row.label}</strong>
                                    </td>
                                    <td />
                                    <td />
                                    <td />
                                    <td />
                                </tr>
                            );
                        }

                        if (row.kind === "subtotal") {
                            return (
                                <tr key={row.id}>
                                    <td />
                                    <td />
                                    <td />
                                    <td />
                                    <td className="nowrap right">
                                        <strong>{formatImpact(row.impact)}</strong>
                                    </td>
                                </tr>
                            );
                        }

                        if (row.kind === "carry") {
                            return (
                                <tr key={row.id}>
                                    <td className="nowrap left pl-4">{row.label}</td>
                                    <td className="nowrap right">{row.weightLabel || "-"}</td>
                                    <td className="nowrap right">{row.duration || "-"}</td>
                                    <td className="nowrap right">{row.change || "-"}</td>
                                    <td className="nowrap right">{formatImpact(row.impact)}</td>
                                </tr>
                            );
                        }

                        if (row.kind === "total") {
                            return (
                                <tr key={row.id}>
                                    <td className="nowrap left">
                                        <strong>{row.label}</strong>
                                    </td>
                                    <td />
                                    <td />
                                    <td />
                                    <td className="nowrap right">
                                        <strong>{formatImpact(row.impact)}</strong>
                                    </td>
                                </tr>
                            );
                        }

                        return (
                            <tr key={row.id}>
                                <td className="nowrap left pl-4">
                                    <FoNavInstrumentName name={row.label} instrumentId={row.instrumentId} />
                                </td>
                                <td className="nowrap right">
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        className={inputClassName}
                                        value={row.weightLabel}
                                        onChange={(event) => updateInput(row.id, "weight", event.target.value)}
                                    />
                                </td>
                                <td className="nowrap right">
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        className={inputClassName}
                                        value={row.duration}
                                        onChange={(event) => updateInput(row.id, "duration", event.target.value)}
                                    />
                                </td>
                                <td className="nowrap right">
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        className={inputClassName}
                                        value={row.change}
                                        onChange={(event) => updateInput(row.id, "change", event.target.value)}
                                    />
                                </td>
                                <td className="nowrap right">{formatImpact(row.impact)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {calendarEventsMissing ? (
                <p className="text-[9pt] text-slate-500 mt-1">
                    No SwedenFixedIncome calendar found for {calendarYears.join(", ")}. Carry settlement dates skip weekends only.
                </p>
            ) : null}
        </div>
    );
}

interface FoNavEstimateProps {
    fundId: string;
    startDate: string;
    endDate: string;
    prevNavDate?: string | null;
    currentNavDate?: string | null;
    totalNav: number | null;
    rateSource?: FoNavRateSource;
    sebRates?: FoNavSebRateInput[];
    onEstimateChange?: (values: FoNavEstimateKpiValues) => void;
}
